import { useCreateSubjectNameGetter } from '@/shared/category/useCreateSubjectNameGetter';
import { useCachedCategoryInfo } from '@/shared/category/useFetchCategoryInfo';
import { PublisherLabelMap } from '@/shared/category/util';
import { Chip, SchoolInfoChip } from '@/shared/components/Chip/QuizInfoChip';
import { cn } from '@/utils/tailwind.util';
import type { FolderChip } from '../types/folder.types';
export type FolderChipsSize = 'card' | 'hero';

/**
 * ZUS 는 `@media (max-width: 560px)` 에서 타이포 클래스를 한 단계 줄인다
 * (`packages/zus/src/tailwind.preset.ts`). 그래서 **폰트는 분기하지 않는다** —
 * `body-sm-semibold` 하나가 곧 13px/12px 이고, 분기를 더하면 축소가 이중으로 걸린다.
 * 반면 **패딩은 자동축소 대상이 아니라** 직접 분기한다(시안 모바일 칩은 `px-2xs`).
 *
 * 축소 시점만 자리마다 다르다 — 카드는 `tablet-lg`, 히어로는 `tablet-sm` 에서 갈린다.
 */
const CHIP_TEXT_BY_SIZE: Record<FolderChipsSize, string> = {
  card: 'text-body-sm-semibold max-tablet-lg:px-2xs',
  hero: 'text-body-sm-semibold max-tablet-sm:px-2xs'
};
interface FolderCategoryChipsProps {
  chips: FolderChip[];
  size?: FolderChipsSize;
  emptyPlaceholderClassName?: string;
  className?: string;
}
export const FolderCategoryChips = ({
  chips,
  size = 'card',
  emptyPlaceholderClassName,
  className
}: FolderCategoryChipsProps) => {
  const chipText = CHIP_TEXT_BY_SIZE[size];
  const {
    categoryTreeData
  } = useCachedCategoryInfo();
  const getSubjectName = useCreateSubjectNameGetter(categoryTreeData?.subjectTree);
  if (chips.length === 0) {
    if (!emptyPlaceholderClassName) return null;
    return <div aria-hidden className={cn('flex flex-wrap gap-3xs', emptyPlaceholderClassName)}>
        <Chip className={cn(chipText, 'invisible')}>{'\u00A0'}</Chip>
      </div>;
  }
  return <div className={cn('flex flex-wrap gap-3xs', className)} data-sentry-component="FolderCategoryChips" data-sentry-source-file="FolderCategoryChips.tsx">
      {chips.map(chip => {
      if (chip.kind === 'SCHOOL') {
        return <SchoolInfoChip key={chip.kind} className={chipText} schoolInfo={{
          schoolType: chip.schoolType ?? '',
          schoolGrade: chip.schoolGrade ?? '',
          schoolSemester: chip.schoolSemester
        }} otherCount={chip.otherCount} />;
      }
      const label = chip.kind === 'SUBJECT' ? getSubjectName({
        subjectGroupId: chip.subjectId,
        subSubjectGroupId: undefined
      }) : PublisherLabelMap[chip.publisherId];
      if (!label) return null;
      return <Chip key={chip.kind} className={chipText}>
            <span>{label}</span>
            <OtherCount count={chip.otherCount} />
          </Chip>;
    })}
    </div>;
};
const OtherCount = ({
  count
}: {
  count: number;
}) => count > 0 ? <span className={'ml-4xs text-assistive'}>{`외 ${count}`}</span> : null;